import React, { useEffect } from "react";
import {
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";
import { XIcon } from "@heroicons/react/solid";
import { Trans } from "@lingui/macro";
import { TransProps } from "@lingui/react";
import { getColor } from "components/utils";

// Type
export type typeNotification = {
  variant?: string;
  deleteNotification: () => void;
  type: "warning" | "success" | "error" | "info";
  title?: React.ReactElement<TransProps> | string;
  message: React.ReactElement<TransProps> | string;
};

// Component
export const Notification: React.FC<typeNotification> = ({
  variant,
  deleteNotification,
  type,
  title,
  message,
}) => {
  const color = getColor(variant);

  let borderColor;
  let textColor;
  let bgColor;
  let bgColorDark;
  let offsetColor;

  switch (type) {
    case "warning":
      borderColor = "border-yellow-100";
      textColor = "text-yellow-400";
      bgColor = "bg-yellow-50";
      bgColorDark = "bg-yellow-100";
      offsetColor = "sm:ring-offset-yellow-100";
      break;
    case "success":
      borderColor = "border-green-100";
      textColor = "text-green-400";
      bgColor = "bg-green-50";
      bgColorDark = "bg-green-100";
      offsetColor = "sm:ring-offset-green-100";
      break;
    case "error":
      borderColor = "border-red-100";
      textColor = "text-red-400";
      bgColor = "bg-red-50";
      bgColorDark = "bg-red-100";
      offsetColor = "sm:ring-offset-red-100";
      break;
    default:
      borderColor = "border-blue-100";
      textColor = "text-blue-400";
      bgColor = "bg-blue-50";
      bgColorDark = "bg-blue-100";
      offsetColor = "sm:ring-offset-blue-100";
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      deleteNotification();
      clearTimeout(timer);
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div className={`w-full flex flex-col items-center space-y-4`}>
      <div
        className={`max-w-md w-full ${bgColor} shadow-lg border-t ${borderColor} rounded-lg pointer-events-auto overflow-hidden`}
      >
        <div className="p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {type === "success" ? (
                <CheckCircleIcon
                  className={`h-6 w-6 ${textColor}`}
                  aria-hidden="true"
                />
              ) : null}
              {type === "error" ? (
                <XCircleIcon
                  className={`h-6 w-6 ${textColor}`}
                  aria-hidden="true"
                />
              ) : null}
              {type === "warning" ? (
                <ExclamationCircleIcon
                  className={`h-6 w-6 ${textColor}`}
                  aria-hidden="true"
                />
              ) : null}
              {type === "info" ? (
                <InformationCircleIcon
                  className={`h-6 w-6 ${textColor}`}
                  aria-hidden="true"
                />
              ) : null}
            </div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              {title ? (
                <p className="text-sm font-medium text-gray-900 mb-1">
                  {title}
                </p>
              ) : null}
              <p className="text-sm text-gray-500">{message}</p>
            </div>
            <div className="ml-4 flex-shrink-0 flex">
              <button
                className={`${bgColorDark} rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 ${offsetColor} focus:ring-offset-2 focus:ring-${color}-500`}
                onClick={() => {
                  deleteNotification();
                }}
              >
                <span className="sr-only">
                  <Trans>Close</Trans>
                </span>
                <XIcon className={`h-5 w-5 ${bgColor}`} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
